import { type ReactNode } from 'react';
import { Lock, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ApolloError } from '@apollo/client';
import {
  useEngagementPrivacy,
  type EngagementPrivacyData,
} from '@/lib/hooks/use-engagement-privacy';

interface Props {
  engagementType: 'email' | 'meeting';
  engagement: EngagementPrivacyData | null | undefined;
  loading?: boolean;
  /** The detail query's error: the backend denies a by-id read the viewer
   * may not see with NOT_AUTHORIZED, and that must render as the lock. */
  error?: ApolloError;
  children: ReactNode;
}

/**
 * The engagement noun is baked into each sentence rather than interpolated:
 * languages inflect "email"/"meeting" differently inside the phrase, so a
 * `{{type}}` placeholder would produce ungrammatical copy.
 */
const PRIVACY_COPY = {
  email: { title: 'meetings.privacyTitleEmail', body: 'meetings.privacyBodyEmail' },
  meeting: { title: 'meetings.privacyTitleMeeting', body: 'meetings.privacyBodyMeeting' },
} as const;

export default function EngagementPrivacyGate({
  engagementType,
  engagement,
  loading: externalLoading,
  error,
  children,
}: Props) {
  const { canView, loading: privacyLoading } = useEngagementPrivacy(engagement);
  const { t } = useTranslation();
  const copy = PRIVACY_COPY[engagementType];
  const denied = error?.graphQLErrors?.some(e => e.extensions?.code === 'NOT_AUTHORIZED') ?? false;

  if (!denied && (externalLoading || privacyLoading)) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (denied || !canView) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-3">
        <div className="rounded-full bg-muted p-3">
          <Lock className="h-5 w-5 text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold">{t(copy.title)}</h3>
        <p className="text-xs text-muted-foreground max-w-[280px]">{t(copy.body)}</p>
      </div>
    );
  }

  return <>{children}</>;
}
